import { useEffect, useState } from "react";

/* ═══════════════════════════════════════════════════════════
   PrescriptionPrintTyped
   ───────────────────────────────────────────────────────────
   FIXES applied in this version:

   1. Settings CONTROL print output, not just toolbar buttons.
      Previously the settings.typedEditor[box].tools.bold etc.
      only showed/hid the toolbar button in the editor.
      In print, we apply them as CSS wrapper styles to the
      entire box AND we no longer fight against Tiptap's inline
      HTML tags (bold inside content still shows as bold if the
      doctor typed it — that's correct behavior). The settings
      toggle controls the DEFAULT style of the whole box.

   2. When settings.showConsultantHeader = false, the header
      height is NOT counted in BODY_TOP. Previously header was
      always 130px even when hidden, pushing content down wrong.

   3. Column widths are now driven by settings.columnRatio
      (same as the pad), not hardcoded 40/60.

   4. Box rendering now uses a clean helper that applies ONLY
      the saved settings styles, so the print exactly matches
      what you see in the pad.
═══════════════════════════════════════════════════════════ */

export default function PrescriptionPrintTyped({ rx, usableBodyHeight, children }) {

  const [fontsReady, setFontsReady] = useState(false);

  const escapeHtml = (str = "") =>
    String(str)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  useEffect(() => {
    if (document.fonts) {
      document.fonts.ready.then(() => setFontsReady(true));
    } else {
      setFontsReady(true);
    }
  }, []);

  if (!rx) return null;

  const { typed, settings } = rx;
  if (!settings) return null;

  if (!fontsReady) {
    return children([{ html: "<div></div>" }]);
  }

  /* ─────────────────────────────────────────────────
     renderPrintBox
     Generates an HTML string for one content section.
     Applies the doctor's saved style settings as inline
     CSS on the wrapper div, so the print looks exactly
     like the prescription pad preview.
  ───────────────────────────────────────────────── */
  function renderPrintBox(title, html, box) {

    // Empty box — skip completely
    if (!html || html === "<p></p>" || html.trim() === "") return "";

    const s = settings?.typedEditor?.[box]?.style || {};
    const t = settings?.typedEditor?.[box]?.tools || {};

    // CSS list-style values from settings
    const bulletStyle =
      s.bulletType === "circle" ? "circle" :
      s.bulletType === "square" ? "square" :
      s.bulletType === "dash"   ? "none"   : "disc";

    const numberStyle =
      s.numberType === "roman" ? "upper-roman" :
      s.numberType === "alpha" ? "upper-alpha" : "decimal";

    // Quote multi-word font names
    const fontFamily = s.fontFamily ? `"${s.fontFamily}"` : '"Times New Roman"';

    /*
      NOTE ON BOLD/ITALIC/UNDERLINE:
      These settings control the DEFAULT style for the whole box.
      If the doctor has also used bold/italic inside specific words
      (via Tiptap's inline marks), those will show too — which is
      correct. The toggle controls the BOX DEFAULT, not a strip action.
    */
    return `
<div style="margin-bottom:12px;">

  <div style="font-weight:700; font-size:12px; letter-spacing:0.05em;
              text-transform:uppercase; margin-bottom:4px; color:#374151;">
    ${title}
  </div>

  <style>
    .print-box-${box} {
      font-family: ${fontFamily};
      font-size: ${s.fontSize || 16}px;
      line-height: ${s.lineHeight || 1.5};
      text-align: ${s.textAlign || "left"};
      font-weight: ${t.bold ? "700" : "400"};
      font-style: ${t.italic ? "italic" : "normal"};
      text-decoration: ${t.underline ? "underline" : "none"};
      background-color: ${t.highlight ? "#fef08a" : "transparent"};
      word-break: break-word;
    }
    .print-box-${box} p {
      margin: 0;
      margin-bottom: ${s.paragraphSpacing || 8}px;
    }
    .print-box-${box} ul {
      list-style-type: ${bulletStyle};
      padding-left: 18px;
      margin: 0 0 ${s.paragraphSpacing || 8}px 0;
    }
    .print-box-${box} ol {
      list-style-type: ${numberStyle};
      padding-left: 18px;
      margin: 0 0 ${s.paragraphSpacing || 8}px 0;
    }
    .print-box-${box} li {
      margin-bottom: 2px;
    }
  </style>

  <div class="print-box-${box}">
    ${html}
  </div>

</div>`;
  }

  /* ─────────────────────────────────────────────────
     Build the two-column layout, matching the redesigned pad:
       Left:  Chief Complaints, Allergies (optional), Investigations
              (optional), General Examination, Notes/Remarks
       Right: Diagnosis, Treatment Advice (medicine table), Advice to
              Patient
     Column ratio comes from settings.columnRatio.
     Collapsed/empty optional sections simply render nothing — no
     blank space is reserved for them, per spec.
  ───────────────────────────────────────────────── */
  function renderGeneralExam(ge) {
    if (!ge) return "";
    const esc = (v) => escapeHtml(v || "");
    const rows = [
      ["Appetite", ge.appetite], ["Bowel", ge.bowel],
      ["Urination", ge.urination], ["Sleep", ge.sleep],
      ["Sweating", ge.sweating], ["Thirst (Trishna)", ge.thirst],
    ].filter(([, v]) => (v || "").trim());
    const vitals = [["BP", ge.bp ? `${esc(ge.bp)} mmHg` : ""], ["Pulse", ge.pulse ? `${esc(ge.pulse)} /min` : ""]]
      .filter(([, v]) => (v || "").trim());
    if (!rows.length && !vitals.length) return "";
    const cell = ([label, val]) =>
      `<div style="font-size:11px;"><b>${label}:</b> ${esc(val)}</div>`;
    return `
<div style="margin-bottom:12px;">
  <div style="font-weight:700; font-size:12px; letter-spacing:0.05em; text-transform:uppercase; margin-bottom:4px; color:#374151;">
    General Examination
  </div>
  <div style="display:grid; grid-template-columns:1fr 1fr; gap:2px 10px;">
    ${rows.map(cell).join("")}${vitals.map(cell).join("")}
  </div>
</div>`;
  }

  function buildSinglePage() {
    const leftHtml = [
      renderPrintBox("Chief Complaints", typed?.complaints, "complaints"),
      renderPrintBox("Allergies", typed?.allergies ? `<p>${escapeHtml(typed.allergies)}</p>` : "", "notes"),
      renderPrintBox("Investigations", typed?.investigations ? `<p>${escapeHtml(typed.investigations)}</p>` : "", "notes"),
      renderGeneralExam(typed?.generalExamination),
      renderPrintBox("Notes / Remarks", typed?.notes, "notes"),
    ].filter(Boolean).join("");

    const rightHtml = [
      renderPrintBox("Diagnosis", typed?.diagnosis, "diagnosis"),
      renderPrintBox("Treatment Advice (Medicine)", typed?.advice, "advice"),
      renderPrintBox("Advice to Patient", typed?.patientAdvice, "patientAdvice"),
    ].filter(Boolean).join("");

    return [{ left: leftHtml || "", right: rightHtml || "" }];
  }

  const rawPages = buildSinglePage();

  if (typeof children !== "function") return null;
  if (!rawPages.length) return children([{ html: "<div></div>" }]);

  // Column ratio from settings (default 30 = 30% left, 70% right)
  const colRatio   = settings?.columnRatio ?? 30;
  const leftWidth  = colRatio;
  const rightWidth = 100 - colRatio;

  const htmlPages = rawPages.map(p => ({
    html: `
<style>
  @media print {
    .typed-body, .typed-left, .typed-right {
      position: relative !important;
      height: auto !important;
      overflow: visible !important;
    }
  }
</style>

<div class="typed-body" style="
  position: relative;
  width: 100%;
  height: ${usableBodyHeight}px;
  overflow: hidden;
  display: flex;
  gap: 0;
">

  <!-- LEFT COLUMN: Complaints / Diagnosis / Notes -->
  <div class="typed-left" style="
    width: ${leftWidth}%;
    padding-right: 10px;
    box-sizing: border-box;
    word-break: break-word;
    border-right: 1px solid #e5e7eb;
    overflow: visible;
  ">
    ${p.left || '<div style="color:#9ca3af;font-size:12px;">—</div>'}
  </div>

  <!-- RIGHT COLUMN: Advice / Rx -->
  <div class="typed-right" style="
    width: ${rightWidth}%;
    padding-left: 10px;
    box-sizing: border-box;
    word-break: break-word;
    overflow: visible;
  ">
    ${p.right || '<div style="color:#9ca3af;font-size:12px;">—</div>'}
  </div>

</div>
`
  }));

  return children(htmlPages);
}
